import {datadogLogs} from '@datadog/browser-logs';
import {type DatadogLogs} from '@datadog/browser-logs';
import {once} from 'lodash';

import config from '../../config';
import {
  DATADOG_CLIENT_TOKEN,
  DATADOG_SITE,
  DATADOG_UI_SERVICE,
} from './constants';

/**
 * Initializes Datadog LOGS with configuration for session sampling and error forwarding.
 * This function is wrapped with `once()` to ensure it only runs once.
 * Session sample rate is 100% in debug mode, otherwise 25% of sessions.
 */
export const initDatadogLogs = once(function initDatadogLogs() {
  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    env: window.CONFIG?.ENVIRONMENT_NAME,
    version: window.CONFIG?.GIT_TAG,
    site: DATADOG_SITE,
    forwardErrorsToLogs: true,
    // v7 decoupled console forwarding from forwardErrorsToLogs; keep forwarding
    // console.error to preserve pre-v7 behavior.
    forwardConsoleLogs: ['error'],
    sessionSampleRate: config.DATADOG_DEBUG ? 100 : 25, // only send logs for X% of _sessions_
    service: DATADOG_UI_SERVICE,
  });
});

/**
 * Returns the Datadog Logs global instance for logging operations.
 *
 * @returns The Datadog Logs global instance
 */
export function getDatadogLogs(): DatadogLogs {
  return datadogLogs;
}

/**
 * Records an info log to Datadog Logs. The sanctioned entry point for info
 * logs — do not call `datadogLogs.logger.info` directly, keeping call sites
 * consistent and leaving one place to evolve behavior (sampling, redaction, ...)
 * later.
 *
 * @param message - the log message
 * @param context - optional context attached to the log
 */
export function recordDatadogLogInfo(
  message: string,
  context?: Record<string, unknown>
): void {
  getDatadogLogs().logger.info(message, context);
}
